import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import * as moment from 'moment';
import 'moment/locale/pt-br';

import DateRangePicker from '~/components/DateRangePicker';
import Header from '~/components/Header';
import NPSForm from '~/components/NPSForm';
import SalesTerms from '~/components/SalesTerms';
import { api, maisVendasApi } from '~/services/api';
import Snackbar from '~/util/SnackBar';
import {
    onlineChannels,
    physicalChannels,
} from '~/util/mapChannelsPhysicalAndOnline';
import {
    formatPrice,
    formatWithCent,
    formatCurrecyStringToNumberString,
    formatDecimalToPercentage,
} from '~/util/format';
import { shouldRemoveExternalProducts } from '~/util/functions';
import { handleOrdersResponse } from '~/util/handleOrdersResponse';
import getDateRange from '~/util/getDateRange';
import getAssessmentDatesByDate from '~/util/getAssessmentDatesByDate';
import { GERENTE_ADOROCODIGO } from '~/util/perfilId';
import { changeShouldShowNPS } from '../../store/modules/user/actions';
import { signOut } from '../../store/modules/auth/actions';
import DesktopLayout from './DesktopLayout';

// Antes desta data, produtos externos Off Premium não tinham lógica implementada.
const EXTERNAL_OFFPREMIUM_DATE_LIMIT = '2024-09-27';

function StoreCota() {
    const dispatch = useDispatch();
    const userProfile = useSelector(state => state.user.profile);
    const { initialDate, finalDate, period } = useSelector(
        state => state.status
    );

    const [aggregatedCapturedOrders, setAggregatedCapturedOrders] = useState(0);
    const [capturedOrdersIndicators, setCapturedOrdersIndicators] = useState(
        {}
    );
    const [totalOrdersIndicators, setTotalOrdersIndicators] = useState({});
    const [indicatorsText, setIndicatorsText] = useState({});
    const [quota, setQuota] = useState({});
    const [salesPercentage, setSalesPercentage] = useState('');
    const [salesValueAsNumber, setSalesValueAsNumber] = useState(0);
    const [orders, setOrders] = useState([]);
    const [ordersLoaded, setOrdersLoaded] = useState(false);
    const [balanceText, setBalanceText] = useState(0);
    const [panelLoaded, setPanelLoaded] = useState(false);
    const [chartData, setChartData] = useState([]);
    const [aggregatedOrdersByPeriod, setAggregatedOrdersByPeriod] = useState(
        []
    );
    const [showNPSForm, setShowNPSForm] = useState(false);
    const [shouldLogout, setShouldLogout] = useState(false);
    const [brandFilter, setBrandFilter] = useState('myBrands');

    // Guarda a quota do mês inteiro para uso no modo 'day'
    const monthQuotaRef = useRef(null);

    // Guarda o total de vendas do mês para uso no modo 'day'
    const monthSalesRef = useRef(null);

    const dateRange = getDateRange(initialDate, finalDate, 'DD/MM/YYYY');

    const filialCode = (() => {
        const code = userProfile?.users_branches[0]?.codigo_filial;
        return code?.length === 3 ? `000${code}` : code;
    })();

    const isAdoroCodigoManager = userProfile?.id_perfil === GERENTE_ADOROCODIGO;
    const apiClient = isAdoroCodigoManager ? maisVendasApi : api;
    const routes = {
        aggregatedOrders: isAdoroCodigoManager
            ? '/trainersmanager/aggregated-orders'
            : '/store/aggregated-orders',
        ordersList: isAdoroCodigoManager
            ? '/trainersmanager/orders'
            : '/store/orders-list',
    };

    useEffect(() => {
        if (shouldLogout) dispatch(signOut());
    }, [dispatch, shouldLogout]);

    useEffect(() => {
        const isFarm = userProfile?.marcaFilial === 2;
        const beforeDateLimit = new Date() < new Date('2023-05-26');
        if (userProfile?.shouldAnswerNPS && !(beforeDateLimit && isFarm)) {
            setShowNPSForm(true);
        }
    }, [userProfile]);

    const checkIfCanMakeRequests = useCallback(
        () => finalDate && userProfile?.users_sales_code[0].codigo_vendedor,
        [finalDate, userProfile]
    );

    const getBrandsParamsForRequests = useCallback(() => {
        const allBrandsIds = Array.from({ length: 16 }, (_, i) => i + 1);
        const myBrands = userProfile.users_brands.map(b => b.id_marca);
        switch (brandFilter) {
            case 'allBrands':
                return [];
            case 'myBrands':
                return myBrands;
            case 'otherBrands':
                return allBrandsIds.filter(id => !myBrands.includes(id));
            default:
                return [];
        }
    }, [brandFilter, userProfile]);

    useEffect(() => {
        const indicators = {
            totalValue: 0,
            totalAverageValue: 0,
            totalOrderQuantity: 0,
            totalProductQuantity: 0,
            totalAverageQuantity: 0,
            onlineTotalValue: 0,
            onlineAverageValue: 0,
            onlineAverageQuantity: 0,
            onlineOrderQuantity: 0,
            onlineProductQuantity: 0,
            physicalTotalValue: 0,
            physicalAverageValue: 0,
            physicalAverageQuantity: 0,
            physicalOrderQuantity: 0,
            physicalProductQuantity: 0,
        };

        Object.entries(capturedOrdersIndicators).forEach(([channel, value]) => {
            if (onlineChannels.includes(channel)) {
                indicators.onlineTotalValue += value.value;
                indicators.onlineOrderQuantity += value.serviceQuantity;
                indicators.onlineProductQuantity += value.productQuantity;
            }
            if (physicalChannels.includes(channel)) {
                indicators.physicalTotalValue += value.value;
                indicators.physicalOrderQuantity += value.serviceQuantity;
                indicators.physicalProductQuantity += value.productQuantity;
            }
        });

        indicators.onlineAverageQuantity = (
            indicators.onlineProductQuantity / indicators.onlineOrderQuantity ||
            0
        ).toFixed(2);
        indicators.onlineAverageValue =
            indicators.onlineTotalValue / indicators.onlineOrderQuantity || 0;
        indicators.physicalAverageQuantity = (
            indicators.physicalProductQuantity /
                indicators.physicalOrderQuantity || 0
        ).toFixed(2);
        indicators.physicalAverageValue =
            indicators.physicalTotalValue / indicators.physicalOrderQuantity ||
            0;

        if (userProfile && Object.keys(totalOrdersIndicators).length > 0) {
            const total = totalOrdersIndicators[userProfile.marcaFilial];
            if (total) {
                indicators.totalValue = total.value;
                indicators.totalOrderQuantity = total.serviceQuantity;
                indicators.totalProductQuantity = total.productQuantity;
                indicators.totalAverageValue =
                    total.value / total.serviceQuantity;
                indicators.totalAverageQuantity = (
                    total.productQuantity / total.serviceQuantity
                ).toFixed(2);
            }
        }

        const monetaryFields = [
            'onlineTotalValue',
            'physicalTotalValue',
            'totalValue',
            'totalAverageValue',
            'physicalAverageValue',
            'onlineAverageValue',
        ];
        monetaryFields.forEach(key => {
            indicators[key] = formatWithCent(indicators[key]);
        });

        setIndicatorsText(indicators);
    }, [capturedOrdersIndicators, totalOrdersIndicators, userProfile]);

    useEffect(() => {
        if (aggregatedCapturedOrders && aggregatedCapturedOrders[0] === '-') {
            return setSalesPercentage('0%');
        }
        const salesValue = formatCurrecyStringToNumberString(
            aggregatedCapturedOrders
        );
        const quotaValue = formatCurrecyStringToNumberString(quota.totalQuota);
        setSalesPercentage(
            formatDecimalToPercentage(Number(salesValue) / Number(quotaValue))
        );
    }, [aggregatedCapturedOrders, quota.totalQuota]);

    useEffect(() => {
        if (!checkIfCanMakeRequests()) return;

        setOrders([]);
        setPanelLoaded(false);
        setOrdersLoaded(false);

        const fetchMonthQuota = async () => {
            const assessmentDates = getAssessmentDatesByDate(
                moment(finalDate),
                userProfile.marcaFilial
            );
            const startDate =
                assessmentDates?.startDate ??
                moment(finalDate).startOf('month');
            const endDate =
                assessmentDates?.endDate ?? moment(finalDate).endOf('month');

            const response = await api.get('/store/quota', {
                params: {
                    startDate: startDate.format('YYYY-MM-DD'),
                    endDate: endDate.format('YYYY-MM-DD'),
                    filialCode,
                    brand: userProfile.marcaFilial,
                },
            });

            monthQuotaRef.current = response.data;
            return response.data;
        };

        const fetchMonthSales = async () => {
            const assessmentDates = getAssessmentDatesByDate(
                moment(finalDate),
                userProfile.marcaFilial
            );
            const startDate =
                assessmentDates?.startDate ??
                moment(finalDate).startOf('month');
            const endDate =
                assessmentDates?.endDate ?? moment(finalDate).endOf('month');

            const params = {
                startDate: startDate.format('YYYY-MM-DD'),
                endDate: endDate.format('YYYY-MM-DD'),
                filialCode,
                ...(isAdoroCodigoManager
                    ? { brands: userProfile.marcaFilial }
                    : { filialBrandId: userProfile.marcaFilial }),
                removeExternalProducts: shouldRemoveExternalProducts(
                    userProfile.marcaFilial
                ),
            };

            if ([2, 6].includes(userProfile.marcaFilial)) {
                delete params.removeExternalProducts;
                params.farmLatam = 'exclude';
                params.offExternal = 'exclude';
            }

            const response = await apiClient.get(routes.aggregatedOrders, {
                params,
            });
            const totalValue =
                response.data.data.total.value +
                response.data.data.returns.value;

            monthSalesRef.current = Number(totalValue) * 100;
        };

        const getSalespersonQuota = async () => {
            // Se estiver no modo 'day' e ainda não tiver a quota do mês carregada
            if (period === 'day' && !monthQuotaRef.current) {
                await fetchMonthQuota();
            }

            let quotaStartDate = moment(initialDate);
            let quotaEndDate = moment(finalDate);

            if (period === 'month') {
                const assessmentDates = getAssessmentDatesByDate(
                    moment(finalDate),
                    userProfile.marcaFilial
                );
                if (assessmentDates) {
                    quotaStartDate = assessmentDates.startDate;
                    quotaEndDate = assessmentDates.endDate;
                }
            }

            const response = await api.get('/store/quota', {
                params: {
                    startDate: quotaStartDate.format('YYYY-MM-DD'),
                    endDate: quotaEndDate.format('YYYY-MM-DD'),
                    filialCode,
                    brand: userProfile.marcaFilial,
                },
            });

            // totalQuota recebe quotaUntilEndDate para exibir a barra "Cota até hoje".
            const monthPeriod = response.data.periodQuotas?.find(
                p => p.period === 'month'
            );
            const quotaForDisplay =
                period === 'month' && monthPeriod?.quotaUntilEndDate != null
                    ? monthPeriod.quotaUntilEndDate
                    : response.data.totalQuota;

            const normalizedPeriodQuotas =
                period === 'month'
                    ? response.data.periodQuotas?.map(p =>
                          p.period === 'month'
                              ? { ...p, quota: response.data.totalQuota }
                              : p
                      )
                    : response.data.periodQuotas;

            const quotaData = {
                ...response.data,
                periodQuotas: normalizedPeriodQuotas,
                totalQuotaAsNumber: quotaForDisplay,
                totalQuota: formatPrice(quotaForDisplay),
            };

            if (period === 'month') {
                monthQuotaRef.current = quotaData;
            }

            const mergedQuota =
                period === 'day' && monthQuotaRef.current
                    ? {
                          ...quotaData,
                          dailyQuotas: monthQuotaRef.current.dailyQuotas,
                      }
                    : quotaData;

            setQuota(mergedQuota);
        };

        const getAggregatedCapturedOrders = async () => {
            try {
                const params = {
                    startDate: moment(initialDate).format('YYYY-MM-DD'),
                    endDate: moment(finalDate).format('YYYY-MM-DD'),
                    filialCode,
                    ...(isAdoroCodigoManager
                        ? { brands: userProfile.marcaFilial }
                        : { filialBrandId: userProfile.marcaFilial }),
                    removeExternalProducts: shouldRemoveExternalProducts(
                        userProfile.marcaFilial
                    ),
                };

                // Farm e Off Premium: excluir vendas LATAM e externas
                if ([2, 6].includes(userProfile.marcaFilial)) {
                    delete params.removeExternalProducts;
                    params.farmLatam = 'exclude';
                    params.offExternal = 'exclude';
                }

                if (
                    moment(initialDate).format('YYYY-MM-DD') <=
                    EXTERNAL_OFFPREMIUM_DATE_LIMIT
                ) {
                    delete params.removeExternalProducts;
                    params.offExternal = 'exclude';
                }

                const response = await apiClient.get(routes.aggregatedOrders, {
                    params,
                });
                const totalValue =
                    response.data.data.total.value +
                    response.data.data.returns.value;

                setAggregatedCapturedOrders(formatPrice(totalValue));
                setBalanceText(formatPrice(totalValue));
                setSalesValueAsNumber(Number(totalValue) * 100);
                setCapturedOrdersIndicators(
                    response.data.data.aggregatedByChannel
                );
                setTotalOrdersIndicators(response.data.data.aggregatedByBrand);
                setChartData(
                    getDateRange(initialDate, finalDate, 'YYYY-MM-DD').map(
                        date => ({
                            value:
                                response.data.data.aggregatedByDay[date.label]
                                    ?.value,
                            label: date.formattedLabel,
                        })
                    )
                );
                setAggregatedOrdersByPeriod(response.data.periodSales);

                if (period === 'month') {
                    monthSalesRef.current = Number(totalValue) * 100;
                }

                if (response.data.msg) Snackbar.info(response.data.msg);
            } catch {
                Snackbar.error(
                    'Erro retornando os indicadores de pedidos capturados'
                );
            }
        };

        const getSalespersonOrders = async () => {
            try {
                const params = {
                    startDate: moment(initialDate).format('YYYY-MM-DD'),
                    endDate: moment(finalDate).format('YYYY-MM-DD'),
                    codigo_filial: filialCode,
                    brands: userProfile.marcaFilial,
                    removeExternalProducts: shouldRemoveExternalProducts(
                        userProfile.marcaFilial
                    ),
                };

                if ([2, 6].includes(userProfile.marcaFilial)) {
                    delete params.removeExternalProducts;
                    params.farmLatam = 'exclude';
                    params.offExternal = 'exclude';
                }

                const response = await apiClient.get(routes.ordersList, {
                    params,
                });
                setOrders(
                    await handleOrdersResponse(response.data.data, false)
                );
            } catch {
                Snackbar.error('Erro retornando a lista de pedidos');
                setOrders([]);
            }
        };

        const ensureMonthSales = async () => {
            if (period === 'day' && !monthSalesRef.current) {
                await fetchMonthSales();
            }
        };

        Promise.all([
            ensureMonthSales().then(() => getAggregatedCapturedOrders()),
            getSalespersonQuota(),
            getSalespersonOrders(),
        ])
            .catch(error => {
                if (error.response?.status === 401) {
                    Snackbar.error('Token expirado. Faça login novamente');
                    setShouldLogout(true);
                }
            })
            .finally(() => {
                setPanelLoaded(true);
                setOrdersLoaded(true);
            });
    }, [
        finalDate,
        checkIfCanMakeRequests,
        userProfile,
        getBrandsParamsForRequests,
    ]);

    const handleNPSSubmit = async () => {
        try {
            await api.post('/nps', {
                answer: '',
                comment: '',
                isSalesperson: userProfile.isSalesperson,
                login: userProfile.isSalesperson
                    ? userProfile.salesCode
                    : userProfile.login,
            });
            dispatch(changeShouldShowNPS(false));
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <>
            <NPSForm
                open={showNPSForm}
                onClose={() => {
                    setShowNPSForm(false);
                    handleNPSSubmit();
                }}
            />
            <SalesTerms />
            <Header />

            <DateRangePicker hidden={!panelLoaded} assessment />

            {!panelLoaded && (
                <Grid
                    container
                    justifyContent="center"
                    spacing={1}
                    style={{ paddingTop: '10px' }}
                >
                    <Grid container justifyContent="center" item xs={12}>
                        <Skeleton animation="wave" width="150px" />
                    </Grid>
                    <Grid container justifyContent="center" item xs={12}>
                        <Skeleton animation="wave" width="200px" />
                    </Grid>
                </Grid>
            )}

            <DesktopLayout
                salesNumber={salesValueAsNumber}
                monthlySalesNumber={
                    period === 'day'
                        ? monthSalesRef.current ?? salesValueAsNumber
                        : salesValueAsNumber
                }
                balanceText={balanceText}
                quota={quota}
                salesPercentage={salesPercentage}
                indicatorsText={indicatorsText}
                panelLoaded={panelLoaded}
                ordersLoaded={ordersLoaded}
                orders={orders}
                aggregatedOrdersByPeriod={aggregatedOrdersByPeriod}
                brandFilter={brandFilter}
                setBrandFilter={setBrandFilter}
                chartData={chartData}
                dateRange={dateRange}
                isManagerView
            />
        </>
    );
}

export default StoreCota;
